import { Link } from '@inertiajs/react'
import type { EmailDeliveryMode } from '../../types'

export interface EmailHealthFailure {
  id: number
  event_type: string
  recipient_email: string
  subject: string | null
  reason: string | null
  occurred_at: string | null
  occurred_at_display: string | null
}

export interface EmailHealth {
  status: 'healthy' | 'unhealthy' | 'inactive' | 'off' | 'test_only' | string
  delivery_mode?: EmailDeliveryMode
  message: string | null
  failures: number
  deliveries: number
  since: string | null
  window_seconds: number
  recent_failures: EmailHealthFailure[]
}

interface Props {
  health: EmailHealth | undefined | null
  className?: string
  showRecentFailures?: boolean
  linkToSettings?: boolean
}

export default function EmailHealthBanner({
  health,
  className = '',
  showRecentFailures = false,
  linkToSettings = true,
}: Props) {
  if (!health) return null
  const intentional = health.status === 'off' || health.status === 'test_only'
  if (health.status !== 'unhealthy' && health.status !== 'inactive' && !intentional) return null

  const title =
    health.status === 'off'
      ? 'System email mode: Off.'
      : health.status === 'test_only'
      ? 'System email mode: Test only.'
      : health.status === 'inactive'
      ? 'System email is not configured.'
      : 'System email is failing.'

  const fallback =
    intentional
      ? 'Customer and automated emails are intentionally blocked. Sample results are separate from customer email health.'
      : health.status === 'inactive'
      ? 'No transactional email is being delivered until you configure SendGrid.'
      : `Recent outbound email is failing. Investigate the SendGrid integration before users notice.`

  return (
    <div
      role={intentional ? 'status' : 'alert'}
      data-testid="email-health-banner"
      className={`border ${intentional ? 'border-stone/20 bg-white' : 'border-[#ED1C24]/40 bg-[#FDECEC]'} rounded-lg p-4 flex items-start gap-3 ${className}`}
    >
      <svg
        className={`w-5 h-5 ${intentional ? 'text-text-muted' : 'text-[#ED1C24]'} mt-0.5 flex-shrink-0`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
        />
      </svg>
      <div className="flex-1 text-sm">
        <p className="font-semibold text-text-primary">{title}</p>
        <p className="text-text-muted mt-0.5">{health.message || fallback}</p>

        {!intentional && showRecentFailures && health.recent_failures.length > 0 && (
          <ul className="mt-3 space-y-1 text-xs text-[#5A2A2A]">
            {health.recent_failures.map(f => (
              <li key={f.id} className="flex items-start gap-2">
                <span className="font-mono uppercase text-[10px] tracking-wide bg-[#ED1C24]/15 text-[#ED1C24] rounded px-1.5 py-0.5 flex-shrink-0">
                  {f.event_type}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="font-semibold">{f.recipient_email}</span>
                  {f.subject ? <span className="text-text-muted"> · {f.subject}</span> : null}
                  {f.reason ? <span className="block text-text-muted truncate">{f.reason}</span> : null}
                </span>
                {f.occurred_at_display && (
                  <span className="text-text-muted whitespace-nowrap">{f.occurred_at_display}</span>
                )}
              </li>
            ))}
          </ul>
        )}

        {linkToSettings && (
          <p className="mt-2">
            <Link href="/super_admin/email_setting" className="text-[#ED1C24] font-semibold hover:underline">
              Open email settings →
            </Link>
          </p>
        )}
      </div>
    </div>
  )
}
